import { registerLocaleData } from '@angular/common'
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http'
import de from '@angular/common/locales/de'
import {
  ApplicationConfig,
  importProvidersFrom,
  Injectable,
  provideZoneChangeDetection,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Title } from '@angular/platform-browser'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import {
  provideRouter,
  RouterStateSnapshot,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router'
import {
  IModuleTranslationOptions,
  ModuleTranslateLoader,
} from '@larscom/ngx-translate-module-loader'
import { FORMLY_CONFIG, provideFormlyCore } from '@ngx-formly/core'
import { FormlyPresetModule } from '@ngx-formly/core/preset'
import { FormlyNgZorroAntdModule } from '@ngx-formly/ng-zorro-antd'
import {
  provideTranslateService,
  TranslateLoader,
  TranslateService,
  TranslationObject,
} from '@ngx-translate/core'
import { provideAngularSvgIcon } from 'angular-svg-icon'
import JSON5 from 'json5'
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config'
import { de_DE, provideNzI18n } from 'ng-zorro-antd/i18n'
import { provideHighlightOptions } from 'ngx-highlightjs'
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown'

import { environment } from '../environments/environment'
import { routes } from './app.routes'
import { formlyConfig, registerTranslateExtension } from './formly.config'
import { errorInterceptor } from './interceptors/error.interceptor'
import { jwtInterceptor } from './interceptors/jwt.interceptor'

registerLocaleData(de)

const HttpLoaderFactory = (http: HttpClient): ModuleTranslateLoader => {
  const baseTranslateUrl = './assets/i18n'

  const options: IModuleTranslationOptions = {
    version: Date.now(),
    lowercaseNamespace: true,
    fileParser: {
      fileExtension: 'json5',
      parseFn: (text): TranslationObject => JSON5.parse(text),
    },
    modules: [
      {
        baseTranslateUrl,
        pathTemplate: '{baseTranslateUrl}/{language}/core',
      },
      ...['models', 'pages', 'components'].map((moduleName) => ({
        baseTranslateUrl,
        moduleName: moduleName,
        pathTemplate: '{baseTranslateUrl}/{language}/{moduleName}',
      })),
    ],
  }
  return new ModuleTranslateLoader(http, options)
}

@Injectable({ providedIn: 'root' })
export class TemplatePageTitleStrategy extends TitleStrategy {
  public constructor(private readonly title: Title) {
    super()
  }

  public override updateTitle(routerState: RouterStateSnapshot): void {
    const title = this.buildTitle(routerState)

    if (title !== undefined) {
      this.title.setTitle(
        `${environment.production ? '' : environment.identifier + ': '}OEW Breitband GmbH | ${title}`
      )
    }
  }
}

const ngZorroConfig: NzConfig = {
  message: { nzTop: 120 },
  notification: { nzTop: 40 },
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(FormlyNgZorroAntdModule),
    importProvidersFrom(FormlyPresetModule),
    provideFormlyCore(formlyConfig),
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: registerTranslateExtension,
      deps: [TranslateService],
    },

    provideNzConfig(ngZorroConfig),
    provideHttpClient(withInterceptors([jwtInterceptor, errorInterceptor])),
    provideAngularSvgIcon(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),
    { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true,
          // pedantic: false,
        },
      },
    }),
    provideTranslateService({
      fallbackLang: 'de',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideNzI18n(de_DE),
    importProvidersFrom(FormsModule), // ??
    provideAnimationsAsync(),

    provideHighlightOptions({
      coreLibraryLoader: () => import('highlight.js/lib/core'),
      languages: {
        json: () => import('highlight.js/lib/languages/json'),
      },
    }),
  ],
}
